export enum WebhookSystem {
  AWX = 'awx',
}

export enum FeatureToggleName {
  OPS_CASH = 'OPS_CASH',
  /**
   * When enabled, it will use our custom UI for payout transfers that use endpoints we've setup
   * When disabled, it will use the Airwallex embedded component Pay flow and transaction-scoped
   */
  PAYOUT_TRANSFER = 'PAYOUT_TRANSFER',
  /**
   * When enabled, the charter detail page shows the new compact
   * CharterDetailsHeader summary card. When disabled, it shows the previous
   * CharterSummary card. Defaults to disabled; enabled per-organization.
   */
  CHARTER_DETAILS_HEADER = 'CHARTER_DETAILS_HEADER',
  /**
   * When enabled, the transaction edit pages use the new inspect/commit endpoints
   * (GET /transaction/:transactionId/inspect and PUT /transaction/:transactionId/commit).
   * when disabled, the legacy update transaction endpoints are used.
   * Defaults to disabled; enabled per-organization.
   */
  TRANSACTION_EDIT = 'TRANSACTION_EDIT',
  /**
   * @internal Test-only feature toggle for infrastructure testing.
   *
   * **IMPORTANT**: This toggle must NEVER be:
   * - Used in production code (handlers, services, UI components)
   * - Stored in the database
   * - Shown in any user-facing interface
   *
   * It exists solely to test the feature toggle infrastructure itself
   * (CRUD operations, provider logic, admin UI, etc.).
   *
   * This is a permanent test fixture and should not be removed.
   */
  TEST_FEATURE_TOGGLE = 'TEST_FEATURE_TOGGLE',
  QUICKBOOKS_INTEGRATION = 'QUICKBOOKS_INTEGRATION',
}

export enum FeatureToggleValue {
  ENABLED = 'enabled',
  DISABLED = 'disabled',
}

export type FeatureToggle = {
  name: FeatureToggleName
  value: FeatureToggleValue
}

export type FeatureToggles = Record<FeatureToggleName, FeatureToggleValue>

export type OrganizationFeatureToggle = FeatureToggle & {
  organizationId: string
}

/**
 * Organization feature toggles with merged values.
 * Used for runtime feature checking where you only need the effective value.
 * Values are merged: organization overrides take precedence over global defaults.
 */
export type OrganizationFeatureToggles = {
  organizationId: string
  featureToggles: FeatureToggles
}

/**
 * Organization feature toggles with detailed breakdown showing global defaults and org overrides.
 * Used by admin UI to display which toggles are using global vs organization-specific values.
 *
 * Key distinction:
 * - orgValue: null = Organization follows global default (will change when global changes)
 * - orgValue: "enabled"/"disabled" = Organization has explicit override (pinned, won't follow global changes)
 *
 * This allows admins to see which organizations have special exceptions vs following global policy.
 */
export type OrganizationFeatureTogglesPayload = {
  organizationId: string
  toggles: FeatureTogglesWithDefaults
}

export type UpdateFeatureTogglesBody = {
  toggles: FeatureToggle[] // Array of { name, value } to upsert.
}

export type CreateFeatureToggleRequest = {
  name: FeatureToggleName
  value: FeatureToggleValue
}

export type CreateOrganizationFeatureToggleRequest = CreateFeatureToggleRequest & {
  organizationId: string
}

export type FeatureToggleWithDefault = {
  globalDefault: FeatureToggleValue
  orgValue: FeatureToggleValue | null
}

export type FeatureTogglesWithDefaults = Record<FeatureToggleName, FeatureToggleWithDefault>

export type UpdateOrganizationTogglesBody = {
  toggles: FeatureToggle[] // Array of { name, value } to upsert.
}
